"use client";

import { DeliveryLocationField } from "@/components/cart/DeliveryLocationField";
import { DockOnSiteField } from "@/components/cart/delivery-fields";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  LISTING_DELIVERY_ADDRESS_INPUT_ID,
  useListingBuyState,
} from "./listing-buy-state";

const UNRESOLVED_ADDRESS_MESSAGE =
  "Pick an address from the suggestions, or type the street, city, state, and 5-digit ZIP.";

export interface ListingAddressCheckout {
  totalLabel: string | null;
  termsLabel: string | null;
  ready: boolean;
  onContinue: () => void;
}

export function ListingAddressDialog({
  checkout,
}: {
  checkout?: ListingAddressCheckout;
}) {
  const { addressDialog, closeAddressDialog } = useListingBuyState();
  const continuesToPayment = addressDialog === "buy" && checkout !== undefined;

  return (
    <Dialog
      open={addressDialog !== null}
      onOpenChange={(open) => {
        if (!open) closeAddressDialog();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Where should this ship?</DialogTitle>
        </DialogHeader>
        <ListingAddressFields />
        {continuesToPayment ? (
          <ContinueToPayment checkout={checkout} />
        ) : (
          <DialogFooter>
            <Button
              type="button"
              variant="primary"
              onClick={closeAddressDialog}
            >
              Done
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

function ListingAddressFields() {
  const { delivery, hasDockOnSite, setHasDockOnSite } = useListingBuyState();
  return (
    <div className="space-y-4">
      <DeliveryLocationField
        savedAddressOptions={delivery.savedAddressOptions}
        location={delivery.location}
        addressDisplay={delivery.addressDisplay}
        onSelectSaved={delivery.selectSavedAddress}
        onAddressChange={delivery.onAddressChange}
        onResolvingChange={delivery.onSelectionResolving}
        inputId={LISTING_DELIVERY_ADDRESS_INPUT_ID}
        label="Street address"
        errorMessage={
          delivery.hasUnresolvedInput ? UNRESOLVED_ADDRESS_MESSAGE : undefined
        }
        saveNotice={delivery.addressBookNotice ?? undefined}
      />
      <DockOnSiteField
        hasDockOnSite={hasDockOnSite}
        onHasDockOnSiteChange={setHasDockOnSite}
      />
    </div>
  );
}

function ContinueToPayment({ checkout }: { checkout: ListingAddressCheckout }) {
  return (
    <div className="space-y-3 border-t border-border pt-4">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm text-muted-foreground">Total before tax</span>
        <span className="text-lg font-semibold tabular-nums">
          {checkout.totalLabel ?? "—"}
        </span>
      </div>
      <Button
        type="button"
        variant={checkout.termsLabel ? "primary" : "brand"}
        className="w-full"
        disabled={!checkout.ready}
        onClick={checkout.onContinue}
      >
        {checkout.termsLabel
          ? `Place order on ${checkout.termsLabel}`
          : "Continue to payment"}
      </Button>
    </div>
  );
}
